import {
    COLORS,
    FUEL_TYPES,
    TRANSMISSION_TYPES,
    REGIONS,
    SELLER_TYPES,
    VEHICLE_TYPES_AUTOS,
    VEHICLE_TYPES_UTILITARIOS,
    VEHICLE_CATEGORIES
} from "../constants/Publication";
import type { AgentFilters, MappedFilters } from "../interfaces/agentFilters";
import { FetchingTypes } from "../interfaces/types";


const normalizeString = (str: string): string => {
    return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
};

/**
 * Encuentra coincidencia en un array de opciones
 */
const findMatch = (value: string, options: string[]): string | null => {
    const normalized = normalizeString(value);

    for (const option of options) {
        const normalizedOption = normalizeString(option);
        if (normalizedOption === normalized || normalizedOption.includes(normalized) || normalized.includes(normalizedOption)) {
            return option;
        }
    }

    return null;
};

/**
 * Encuentra coincidencia en un array de opciones
 */
const findMatchFuel = (value: string, options: any[]): string | null => {
    const normalized = normalizeString(value);

    for (const option of options) {
        const normalizedOption = normalizeString(option.name);
        if (normalizedOption === normalized || normalizedOption.includes(normalized) || normalized.includes(normalizedOption)) {
            return option.name;
        }
    }

    return null;
};

/**
 * Encuentra coincidencia en un array de opciones
 */
const findColorMatch = (value: string, options: any[]): string | null => {
    const normalized = normalizeString(value);

    for (const option of options) {
        const normalizedOption = normalizeString(option.name);
        const isFemenine = normalized.endsWith("a");
        if (normalizedOption === normalized || normalizedOption.includes(normalized) || normalized.includes(normalizedOption)) {
            return option.name;
        }
        if (isFemenine) {
            const masculineOption = normalized.slice(0, -1).concat("o");
            if (masculineOption === normalizedOption || masculineOption.includes(normalizedOption) || normalizedOption.includes(masculineOption)) {
                return option.name;
            }
        }
    }

    return null;
};

/**
 * Mapea los tipos de filtros
 */
const mapVehicleCategory = (tipo: string[]): string[] => {
    if (!tipo || tipo.length === 0) return [];

    const mapped: string[] = [];

    for (const t of tipo) {
        const normalized = normalizeString(t);

        if (normalized === "autos" || normalized === "auto") {
            mapped.push("Autos");
        } else if (normalized === "comerciales" || normalized === "comercial" || normalized === "utilitarios" || normalized === "utilitario") {
            mapped.push("Comerciales");
        }
    }

    return mapped;
};


const mapVehicleType = (uso: string[]): 'used' | 'new' | null => {
    if (!uso || uso.length === 0) return null;

    const normalized = normalizeString(uso[0]);

    if (normalized === "usado" || normalized === "usados") {
        return 'used';
    } else if (normalized === "nuevo" || normalized === "nuevos") {
        return 'new';
    }

    return null;
};


const mapSeller = (tipoVenta: string[]): string[] => {
    if (!tipoVenta || tipoVenta.length === 0) return [];

    const mapped: string[] = [];

    for (const tv of tipoVenta) {
        const match = findMatch(tv, SELLER_TYPES);
        if (match) {
            // Capitalizar primera letra
            mapped.push(match.charAt(0).toUpperCase() + match.slice(1));
        }
    }

    return mapped;
};


const mapColors = (colors: string[], colorsList: any): string[] => {
    if (!colors || colors.length === 0) return [];

    const mapped: string[] = [];
    const apiColors = colorsList?.data?.data;
    const hasApiColors = Array.isArray(apiColors) && apiColors.length > 0;

    for (const color of colors) {
        if (hasApiColors) {
            const match = findColorMatch(color, apiColors);
            if (match) {
                mapped.push(match);
                continue;
            }
        }
        // Fallback: buscar en constantes COLORS (son strings simples)
        const matchConst = findMatch(color, COLORS);
        if (matchConst) {
            mapped.push(matchConst);
        }
    }

    return mapped;
};


const mapTransmission = (transmision: string[]): string[] => {
    if (!transmision || transmision.length === 0) return [];

    const mapped: string[] = [];

    for (const t of transmision) {
        const match = findMatch(t, TRANSMISSION_TYPES);
        if (match) {
            mapped.push(match);
        }
    }

    return mapped;
};


const mapType = (carroceria: string[], vehicleCategory?: string[]): string[] => {
    if (!carroceria || carroceria.length === 0) return [];

    const mapped: string[] = [];
    const isComerciales = vehicleCategory && vehicleCategory.includes("Comerciales");
    const primaryVehicleTypes = isComerciales ? VEHICLE_TYPES_UTILITARIOS : VEHICLE_TYPES_AUTOS;
    const secondaryVehicleTypes = isComerciales ? VEHICLE_TYPES_AUTOS : VEHICLE_TYPES_UTILITARIOS;

    for (const c of carroceria) {
        const normalized = normalizeString(c);

        // Mapeos especiales
        if (normalized === "coupe" || normalized === "coupé") {
            mapped.push("Coupé");
            continue;
        }

        if (normalized === "doble cabina" || normalized === "doblecabina" || normalized === "cabina doble" || normalized === "cabina_doble") {
            mapped.push("Cabina doble");
            continue;
        }

        const cWithSpaces = c.replace(/_/g, " ");

        // Buscar primero en el tipo de vehículo principal
        let match = findMatch(cWithSpaces, primaryVehicleTypes);

        // Si no encuentra, buscar en el tipo secundario
        if (!match) {
            match = findMatch(cWithSpaces, secondaryVehicleTypes);
        }

        if (match) {
            const capitalized = match.charAt(0).toUpperCase() + match.slice(1);
            mapped.push(capitalized);
        }
    }

    return mapped;
};


const mapFuel = (combustible: any[], fuels: any): string[] => {
    if (!combustible || combustible.length === 0) return [];

    const mapped: string[] = [];
    const apiFuels = fuels?.data?.data;
    const hasApiFuels = Array.isArray(apiFuels) && apiFuels.length > 0;

    for (const c of combustible) {
        const normalized = normalizeString(c);

        if (hasApiFuels) {
            const match = findMatchFuel(normalized, apiFuels);
            if (match) {
                mapped.push(match);
                continue;
            }
        }
        // Fallback: buscar en constantes FUEL_TYPES (son strings simples)
        const matchConst = findMatch(c, FUEL_TYPES);
        if (matchConst) {
            mapped.push(matchConst);
        }
    }
    return mapped;
};


const mapRegion = (region: string[]): string[] => {
    if (!region || region.length === 0) return [];

    const mapped: string[] = [];

    for (const r of region) {
        const match = findMatch(r, REGIONS);
        if (match) {
            mapped.push(match);
        }
    }

    return mapped;
};


const mapPrice = (precio?: { desde: number | null; hasta: number | null }): { min: string; max: string } | null => {
    if (!precio) return null;

    const { desde, hasta } = precio;

    if (desde !== null || hasta !== null) {
        // Usar 0 cuando desde es null, usar * cuando hasta es null
        const min = desde !== null ? desde.toString() : '0';
        const max = hasta !== null ? hasta.toString() : '*';
        return { min, max };
    }

    return null;
};


export const mapMileage = (kilometraje: string[]): any[] => {
    if (!kilometraje || kilometraje.length === 0) return [];

    const mapped: any[] = [];

    for (const km of kilometraje) {
        const normalized = normalizeString(km);

        if (normalized.includes("0-45000") || normalized.includes("0-45")) {
            mapped.push({ id: 0, name: "0 - 45.000", value: "*,45000" });
        } else if (normalized.includes("45000-75000") || normalized.includes("45-75")) {
            mapped.push({ id: 1, name: "45.000 - 75.000", value: "45000,75000" });
        } else if (normalized.includes("75000-100000") || normalized.includes("75-100")) {
            mapped.push({ id: 2, name: "75.000 - 100.000", value: "75000,100000" });
        } else if (normalized.includes("100000") || normalized.includes("100") || normalized.includes("más de")) {
            mapped.push({ id: 3, name: "más de 100.000", value: "100000,*" });
        }
    }

    return mapped;
};


export const mapAgentFilters = (agentFilters: AgentFilters, colors: any[], fuels: any[]): MappedFilters => {
    const mapped: MappedFilters = {};

    if (agentFilters.tipo && agentFilters.tipo.length > 0) {
        mapped.vehicleCategory = mapVehicleCategory(agentFilters.tipo);
    }

    if (agentFilters.uso && agentFilters.uso.length > 0) {
        mapped.vehicleType = mapVehicleType(agentFilters.uso);
    }

    if (agentFilters.tipoVenta && agentFilters.tipoVenta.length > 0) {
        mapped.seller = mapSeller(agentFilters.tipoVenta);
    }

    if (agentFilters.concesionario && agentFilters.concesionario.length > 0) {
        mapped.dealership = agentFilters.concesionario;
    }

    if (agentFilters.marca && agentFilters.marca.length > 0) {
        mapped.brand = agentFilters.marca;
    }

    if (agentFilters.modelo && agentFilters.modelo.length > 0) {
        mapped.model = agentFilters.modelo;
    }

    if (agentFilters.año && agentFilters.año.length > 0) {
        mapped.year = agentFilters.año;
    }

    if (agentFilters.color && agentFilters.color.length > 0) {
        mapped.color = mapColors(agentFilters.color, colors);
    }

    if (agentFilters.transmision && agentFilters.transmision.length > 0) {
        mapped.transmission = mapTransmission(agentFilters.transmision);
    }

    if (agentFilters.carroceria && agentFilters.carroceria.length > 0) {
        mapped.type = mapType(agentFilters.carroceria, mapped.vehicleCategory);
    }

    if (agentFilters.combustible && agentFilters.combustible.length > 0) {
        mapped.fuel = mapFuel(agentFilters.combustible, fuels);
    }

    if (agentFilters.region && agentFilters.region.length > 0) {
        mapped.region = mapRegion(agentFilters.region);
    }

    if (agentFilters.precio) {
        mapped.price = mapPrice(agentFilters.precio);
    }

    if (agentFilters.kilometraje && agentFilters.kilometraje.length > 0) {
        mapped.mileage = mapMileage(agentFilters.kilometraje);
    }
    if (agentFilters.version && agentFilters.version.length > 0) {
        mapped.version = agentFilters.version;
    }

    return mapped;
};


export const parseAgentConversation = (agentConversation: any, fetchingAgentConversation: FetchingTypes): any | null => {
    try {
        const content = agentConversation?.content;

        if (fetchingAgentConversation !== 'success' || !content) {
            return null;
        }

        let parsed;
        try {
            parsed = JSON.parse(content);
        } catch (error) {
            return null;
        }
        return parsed;

    } catch (error) {
        return null;
    }
};
